import { ApiRequestError } from '@croissants/contracts';
import { createAsyncPanel, EmptyState, ErrorCard, SkeletonRow, Spinner } from '@croissants/ui';
import type { JSX, ReactNode } from 'react';
import { useT } from '../i18n/index';
import { useApiErrorMessage } from '../lib/api';

/**
 * The three states every remote panel can be in. They are components rather than
 * a pattern each screen re-implements, because the failure state is the one an
 * operator meets at the worst moment and it has to carry the request id every
 * time - that id is what turns "it broke" into a log line.
 */

export interface LoadingPanelProps {
  label?: string;
  /**
   * Draw the placeholder rows of the table that is coming instead of a spinner,
   * at this shape. A spinner says "wait"; a skeleton says "wait, and here is
   * where the thing will be" - and it reserves the height, so the row an
   * operator is reaching for does not jump 200px when the data lands at 06:20.
   */
  skeleton?: { readonly rows?: number; readonly columns?: number };
}

export function LoadingPanel({ label, skeleton }: LoadingPanelProps): JSX.Element {
  const t = useT();
  const text = label ?? t('state.loading');
  if (skeleton !== undefined) {
    return <SkeletonRow label={text} rows={skeleton.rows} columns={skeleton.columns} />;
  }
  return (
    <p className="internal-inline internal-muted">
      <Spinner label={text} />
      {text}
    </p>
  );
}

/**
 * Nothing here, and why. `EmptyState` from packages/ui: an empty panel and a
 * panel whose request failed have to look different from each other and from a
 * loaded one, and a bare grey sentence looked like neither.
 */
export function EmptyPanel({ children }: { children?: ReactNode }): JSX.Element {
  const t = useT();
  return <EmptyState className="internal-empty" title={children ?? t('state.empty')} />;
}

export interface ErrorPanelProps {
  error: unknown;
  onRetry?: () => void;
}

/**
 * Failure codes whose server message carries the actionable diagnosis - the
 * provider's own numbers ("delivery window is -22 min; the provider minimum is
 * 20 min"). No static catalogue string can carry them, so for these the
 * server's sentence is shown under the translated one instead of being
 * flattened into "Something went wrong" (OPS-002, RTE-030).
 */
const DIAGNOSABLE_CODES: ReadonlySet<string> = new Set([
  'provider_limit_violated',
  'capability_unavailable',
  // Account creation failures can carry an actionable provider diagnosis.
  'provider_unavailable',
]);

export function ErrorPanel({ error, onRetry }: ErrorPanelProps): JSX.Element {
  const t = useT();
  const messageOf = useApiErrorMessage();
  const diagnosis =
    error instanceof ApiRequestError && DIAGNOSABLE_CODES.has(error.code) && error.message !== ''
      ? error.message
      : null;

  return (
    <ErrorCard
      className="internal-callout internal-callout--danger"
      flat
      error={error}
      title={t('state.error.title')}
      message={messageOf(error)}
      requestIdLabel={(id) => t('state.error.requestId', { id })}
      retryLabel={t('state.error.retry')}
      retrySize="sm"
      {...(onRetry === undefined ? {} : { onRetry })}
    >
      {diagnosis !== null ? <p className="internal-mono internal-muted">{diagnosis}</p> : null}
    </ErrorCard>
  );
}

/**
 * The operations portal's three cards, bound once to the platform's one panel
 * (`createAsyncPanel`, packages/ui/src/async-panel.tsx). A screen's own `empty`
 * node is the sentence inside the empty card; a screen that wants the
 * placeholder rows of the table that is coming passes
 * `pending={<LoadingPanel skeleton={...} />}`.
 */
export const AsyncPanel = createAsyncPanel({
  pending: <LoadingPanel />,
  empty: (custom) => <EmptyPanel>{custom}</EmptyPanel>,
  error: (failure, retry) => <ErrorPanel error={failure} onRetry={retry} />,
});

/** A short confirmation banner after a write. Announced, then it stays put. */
export function ResultBanner({
  tone = 'success',
  children,
}: {
  tone?: 'success' | 'warning' | 'danger';
  children: ReactNode;
}): JSX.Element {
  return (
    <p className={`internal-callout internal-callout--${tone}`} role="status">
      {children}
    </p>
  );
}
